<template>
  <div>
    <ui-modal
      size="crop-image"
      :visible="!hasErrorOnLoadingImage"
      @on-close="handleCloseModal"
      ref="crop-image-ui-modal"
    >
      <template #title>
        <div class="modal__title--tab pb-3">
          <span class="cursor-pointer back" @click="handleCancel">
            <aha-icon class="icon-back" name="arrow-tail"></aha-icon>
          </span>
          <span class="font-bold">{{ $i18nPresenter.t('Edit') }}</span>
        </div>
      </template>

      <template #body>
        <div class="crop-content">
          <img
            ref="canvas"
            :src="imageUrl"
            class="invisible"
            crossOrigin="Anonymous"
            @error="handleImageLoadFailed"
            @load="handleImageLoadSucceeded"
          >
        </div>
        <Uploader v-if="isImageLoading" />
      </template>

      <template #footer>
        <div class="tab-resize">
          <div class="aspect-ratio">
            <span :class="{active: curRatio === 16/9}" @click="handleAspectRatio(16/9, '16:9')">16:9</span>
            <span :class="{active: curRatio === 4/3}" @click="handleAspectRatio(4/3, '4:3')">4:3</span>
            <span :class="{active: curRatio === 2/3}" @click="handleAspectRatio(2/3, '2:3')">2:3</span>
            <span :class="{active: curRatio === 1}" @click="handleAspectRatio(1, '1:1')">1:1</span>
            <span :class="{active: curRatio === null}" @click="handleAspectRatio(null, 'Free')">
              {{ $i18nPresenter.t('free') }}
            </span>
            <span
              v-if="!isOriginalAspectRatio"
              :class="{active: curRatio === initialAspectRatio}"
              @click="handleAspectRatio(initialAspectRatio, 'Auto')"
            >
              Auto
            </span>
          </div>
          <div class="end"></div>
          <div>
            <span @click="handleZoom(0.1)" v-tooltip="tooltipText('Zoom in')">
              <aha-icon viewBox="0 0 30 30" name="zoom-in"></aha-icon>
            </span>
            <span @click="handleZoom(-0.1)" v-tooltip="tooltipText('Zoom out')">
              <aha-icon viewBox="0 0 30 30" name="zoom-out"></aha-icon>
            </span>
          </div>
          <div class="end"></div>
          <div>
            <span @click="handleRotate(-45)" v-tooltip="tooltipText('Rotate Counterclockwise')">
              <aha-icon viewBox="0 0 30 30" name="rotate-right"></aha-icon>
            </span>
            <span @click="handleRotate(45)" v-tooltip="tooltipText('Rotate Clockwise')">
              <aha-icon viewBox="0 0 30 30" name="rotate-left"></aha-icon>
            </span>
          </div>
          <div class="end"></div>
          <div>
            <span @click="handleFlip('Y')" v-tooltip="tooltipText('Flip vertical')">
              <aha-icon viewBox="0 0 30 30" name="flip-horizontal"></aha-icon>
            </span>
            <span @click="handleFlip('X')" v-tooltip="tooltipText('Flip horizontal')">
              <aha-icon viewBox="0 0 30 30" name="flip-vertical"></aha-icon>
            </span>
          </div>
          <div class="end"></div>
          <div class="text">
            <span @click="handleReset()">{{ $i18nPresenter.t('Reset') }}</span>
          </div>
        </div>
        <aha-antd-button
          v-aha-emit-action
          name="save-edit-image-button"
          :submitting="isSubmitting"
          is-loading-inline
          @click="handleSaveButtonClick"
          class="absolute sm:right-4 bottom-6 right-2"
          @mouseup.stop
        >
          <span>{{ $i18nPresenter.t('Save') }}</span>
        </aha-antd-button>
      </template>
    </ui-modal>

    <ModalUploadImageError
      v-if="hasErrorOnLoadingImage || hasErrorOnCroppingImage"
      @handleOkButtonClick="handleOkButtonClick()"
    />
  </div>
</template>

<script>
// node modules
import { mapActions, mapGetters, mapState } from 'vuex'
import Cropper from 'cropperjs'
import * as Sentry from '@sentry/vue'

import Utils from '@/util'
// constants
import constants from '@/constant'
import { PHOTOS_EVENT } from '@/constant/eventActions'

// UI
import Uploader from '@/components/UI/loaders/Uploader'
import Modal from '@/components/UI/modals/Modal'
import ModalUploadImageError from '@/components/UI/modals/UploadImageError.vue'

const { eventCategories } = constants

export default {
  components: {
    Uploader,
    ModalUploadImageError,
    UiModal: Modal,
  },
  props: {
    imageUrl: {
      type: String,
      default: null,
    },
    aspectRatio: {
      type: Number,
      default: NaN,
    },
    cropperData: {
      type: Object,
      default: null,
    },
    initialAspectRatio: {
      type: Number,
      default: 16 / 9,
    },
    isCropForMetaThumbnail: {
      type: Boolean,
      default: false,
    },
    isChangingImage: {
      type: Boolean,
      default: false,
    },
    expires: {
      type: Number,
      default: null,
    },
    updateInBackground: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      ratio: null,
      scale: null,
      cropper: '',
      minScale: 0,
      maxScale: 100,
      maxRatio: 3,
      minRatio: 0.01,
      extension: null,
      isLoading: false,
      isSubmitting: false,
      isImageLoading: true,
      initialRatio: null,
      hasErrorOnLoadingImage: false,
      hasErrorOnCroppingImage: false,
      curRatio: null,
    }
  },
  computed: {
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
    }),
    ...mapGetters(['selectedSlides']),

    isOriginalAspectRatio() {
      const ratios = [16 / 9, 4 / 3, 2 / 3, 1, null]
      return ratios.includes(this.initialAspectRatio)
    },
  },
  watch: {
    ratio(ratio) {
      const midScale = (this.minScale + this.maxScale) / 2

      if (ratio <= this.initialRatio) {
        const ratioPerScale = (this.initialRatio - this.minRatio) / midScale
        this.scale = (ratio - this.minRatio) / ratioPerScale
        return
      }

      const ratioPerScale = (this.maxRatio - this.initialRatio) / midScale
      this.scale = midScale + (ratio - this.initialRatio) / ratioPerScale
    },
    cropper() {
      if (!this.cropperData && this.aspectRatio) {
        this.curRatio = this.aspectRatio
        this.handleAspectRatio(this.aspectRatio)
      }
    },
  },
  mounted() {
    if (!this.imageUrl) {
      this.$emit('on-cancel')
      return
    }

    // cannot edit gif file
    this.extension = this.imageUrl.split('?')[0].split('.').pop()
    if (this.extension === 'gif') {
      this.$emit('on-cancel')
      return
    }

    this.scale = (this.maxScale + this.minScale) / 2
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    handleImageLoadSucceeded() {
      const cropperOptions = {
        checkCrossOrigin: false,
        checkOrientation: false,
        center: true,
        viewMode: 1, // restrict the crop box not to exceed the size of the canvas.
        ready: () => {
          this.isImageLoading = false
          if (this.cropperData) {
            const { canvasData, cropBoxData, initialRatio } = this.cropperData
            this.ratio = canvasData.width / canvasData.naturalWidth
            this.initialRatio = initialRatio || this.ratio
            this.curRatio = initialRatio
            this.cropper.setAspectRatio(initialRatio)
            this.cropper.setCanvasData(canvasData)
            this.cropper.setCropBoxData(cropBoxData)
          } else {
            this.defaultCropperReady()
          }
          // Call 'onCropperReady' to notify listeners that the cropper is ready
          // This allows us to wait for the 'ready' callback execution without modifying the original
          // handleImageLoadSucceeded method, maintaining separation of concerns in the code.
          this.onCropperReady()
        },
        zoom: (event) => {
          const { ratio } = event.detail

          if (ratio < this.minRatio || ratio > this.maxRatio) {
            event.preventDefault()
            return
          }

          this.ratio = ratio
        },
      }

      this.cropper = new Cropper(this.$refs.canvas, cropperOptions)
    },
    onCropperReady() {
      //
    },
    getCroppedImage() {
      let quality = 1
      let extension = 'png'
      let fileType = 'image/png'
      let fillColor = 'transparent'
      let width = 1280
      let height = 720
      const imageW = this.cropper.getImageData().naturalWidth
      if (this.isCropForMetaThumbnail && imageW > 1200) {
        width = 1200
        height = 675
      } else if (imageW >= 2560) {
        width = 2560
        height = 1440
      } else if (imageW > 1280) {
        width = imageW
        height = parseInt((imageW / 16) * 9, 10)
      }
      // cropped image will be jpeg 60%
      if (this.extension === 'jpg' || this.extension === 'jpeg') {
        extension = 'jpg'
        fileType = 'image/jpeg'
        fillColor = this.slideActive.baseColour
        quality = 0.9
      }

      const modifiedName = extension === 'gif'
        ? `${this.extractFileName(this.imageUrl)}.${extension}`
        : `${this.extractFileName(this.imageUrl)}-cropped.${extension}`

      return new Promise((resolve) => {
        this.cropper
          .getCroppedCanvas({
            fillColor,
            height,
            width,
            imageSmoothingQuality: 'high',
          })
          .toBlob(async (blob) => {
            const file = new File([blob], modifiedName, {
              type: fileType,
            })
            file.blob = blob
            resolve(file)
          }, fileType, quality)
      })
    },
    extractFileName(url) {
      const [originImageUrl] = url.split('?')
      const name = originImageUrl.split('/').pop()
      const splitName = name.split('.')

      if (splitName.length < 2) {
        return name
      }

      splitName.pop()
      return splitName.join('.').trim()
    },

    async handleSaveButtonClick() {
      if (this.isSubmitting) return

      this.isSubmitting = true

      const file = await this.getCroppedImage()

      const canvasData = this.cropper.getCanvasData()
      const cropBoxData = this.cropper.getCropBoxData()
      const cropperData = {
        canvasData,
        cropBoxData,
        initialRatio: this.curRatio,
      }
      // We can close the crop modal here, and set the thumbnail rightaway
      // Keep the upload process in the background
      try {
        this.updateInBackground({
          file,
          blob: file.blob,
          accessCode: this.presentation.uniqueAccessCode,
          expires: this.expires || undefined,
          raw: null,
          originImageUrl: this.imageUrl,
          cropperData,
          isChangingImage: this.isChangingImage,
          selectedSlides: this.selectedSlides,
        })
        Utils.trackGA4AndMixpanel(PHOTOS_EVENT.SAVE, {
          eventAction: PHOTOS_EVENT.SAVE,
          eventCategory: eventCategories.PRESENTATION_EDITOR,
          presentationId: this.presentation.id,
          slideId: this.slideActive.id,
          userId: this.currentUser.id,
        })
      } catch (error) {
        Sentry.captureException(error)
        this.isSubmitting = false
        this.hasErrorOnCroppingImage = true
      }
    },
    handleCloseModal() {
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.CLOSE_MODAL, {
        eventAction: PHOTOS_EVENT.CLOSE_MODAL,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
      this.$emit('hideModal')
    },
    handleImageLoadFailed() {
      this.hasErrorOnLoadingImage = true
      Utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Image > Upload > Failed')
    },
    handleOkButtonClick() {
      if (this.hasErrorOnLoadingImage) {
        this.$emit('on-cancel')
        return
      }

      if (this.hasErrorOnCroppingImage) {
        this.hasErrorOnCroppingImage = false
      }
    },
    handleFlip(value) {
      if (value === 'X') {
        if (this.cropper.getData().scaleX === 1) {
          this.cropper.scaleX(-1)
        } else {
          this.cropper.scaleX(1)
        }
      } else if (value === 'Y') {
        if (this.cropper.getData().scaleY === 1) {
          this.cropper.scaleY(-1)
        } else {
          this.cropper.scaleY(1)
        }
      }
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.FLIP, {
        eventAction: PHOTOS_EVENT.FLIP,
        eventLabel: value === 'Y' ? 'Vertical' : 'Horizontal',
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
    handleAspectRatio(ratio, label = false) {
      this.curRatio = ratio
      this.cropper.setAspectRatio(ratio)
      // eslint-disable-next-line use-isnan
      if (ratio === null) {
        this.defaultCropperReady()
      }
      if (label) {
        Utils.trackGA4AndMixpanel(PHOTOS_EVENT.CROP, {
          eventAction: PHOTOS_EVENT.CROP,
          eventLabel: label,
          eventCategory: eventCategories.PRESENTATION_EDITOR,
          presentationId: this.presentation.id,
          slideId: this.slideActive.id,
          userId: this.currentUser.id,
        })
      }
    },
    handleZoom(value) {
      this.cropper.zoom(value)
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.ZOOM, {
        eventAction: PHOTOS_EVENT.ZOOM,
        eventLabel: value > 0 ? 'Zoom in' : 'Zoom out',
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
    handleRotate(value) {
      this.cropper.rotate(value)
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.ROTATE, {
        eventAction: PHOTOS_EVENT.ROTATE,
        eventLabel: value > 0 ? 'Right' : 'Left',
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
    handleReset() {
      this.cropper.reset()
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.RESET, {
        eventAction: PHOTOS_EVENT.RESET,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
    handleCancel() {
      this.$emit('on-cancel')
      Utils.trackGA4AndMixpanel(PHOTOS_EVENT.BACK, {
        eventAction: PHOTOS_EVENT.BACK,
        eventCategory: eventCategories.PRESENTATION_EDITOR,
        presentationId: this.presentation.id,
        slideId: this.slideActive.id,
        userId: this.currentUser.id,
      })
    },
    defaultCropperReady() {
      const cropBoxData = this.cropper.getCropBoxData()
      const canvasData = this.cropper.getCanvasData()

      cropBoxData.left = canvasData.left
      cropBoxData.top = canvasData.top
      cropBoxData.width = canvasData.width
      cropBoxData.height = canvasData.height
      this.cropper.setCropBoxData(cropBoxData)
    },
    tooltipText(text) {
      if (window.innerWidth < 768) {
        return false
      }
      return this.$i18nPresenter.t(text)
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/mixins.scss";
@import "@/assets/scss/variable.scss";

.tab-range {
  width: 100%;
}

.tab-btns {
  width: 80%;
  margin: 0 auto;
  cursor: pointer;
}

.crop-content {
  width: 80%;
  height: 100%;
  padding: 0;
  margin: auto;

  @media (max-width: 767px) {
    height: 100%;
  }
  img {
    max-width: 100%;
  }
}

.loading-crop-image {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background-color: #00000087;
  color: #fff;
}
.tab-resize {
  display: flex;
  font-size: 14px;
  justify-content: center;
  > div {
    display: flex;
    > span {
      width: 48px;
      height: 48px;
      display: flex;
      cursor: pointer;
      font-weight: 600;
      border-radius: 4px;
      align-items: center;
      justify-content: center;

      &:hover {
        background-color: #EFF1F7;
      }
    }
    &.aspect-ratio, &.text {
      > span {
        width: 64px;
      }
      .active {
        color: #fff;
        background-color: #621BAF;
      }
    }
    &.end {
      display: flex;
      padding: 0 20px;
      position: relative;
      align-items: center;
      justify-content: center;
      &:after {
        width: 1px;
        content: "";
        height: 55%;
        // position: absolute;
        background-color: #D3D7E1;

      }
    }
  }
  svg {
    color: #fff;
  }
  @media (max-width: 1200px) {
    width: calc(100% - 108px);
    overflow: auto;
    justify-content: flex-start;
  }
}
.modal__title--tab {
  gap: 10px;
  display: flex;
  .icon-back:hover {
    color: #6A1EBB;
  }
}
.modal-dialog {
  .absolute {
    position: absolute !important;
  }
}
</style>
